import { type LDClient, type LDSingleKindContext, initialize } from 'launchdarkly-js-client-sdk';
import { type ReactNode, createContext, useEffect, useState } from 'react';

import { apiKey, initializationTimeoutSecondsDefault, privateAttributes } from './constants';
import { getAllFlagsSync, handleFlagsInitOrChange } from './flag-cache';
import { getClearUserContext, getUpdateUserContext, getUserContext } from './utils';

const _getFlagChangeHandler = (c: LDClient) => () => {
  handleFlagsInitOrChange(c.allFlags());
};

/**
 * Shared client promise for web initialization.
 */
let _client: Promise<LDClient> = (async () => {
  const context = (await getUserContext()) as LDSingleKindContext;
  const client = initialize(apiKey, context, {
    streaming: true,
    bootstrap: getAllFlagsSync(),
    privateAttributes,
  });
  client.on('initialized', _getFlagChangeHandler(client));
  client.on('change', _getFlagChangeHandler(client));

  return client;
})();

/**
 * Resolve and wait for the initialized LaunchDarkly client.
 */
const getInitializedClient = async (): Promise<LDClient> => {
  const client = await _client;

  try {
    await client.waitForInitialization(initializationTimeoutSecondsDefault);
  } finally {
    // Even if this errors, we don't want to prevent initialization after timeout because LD will try still
    // connect in the background, the user will just be using the app with default flag values.
    return client;
  }
};

export const waitForClientInitialization = async (
  timeoutSeconds: number = initializationTimeoutSecondsDefault
): Promise<void> => {
  const client = await _client;

  try {
    await client.waitForInitialization(timeoutSeconds);
  } catch {
    // noop
    // Even if this errors, we don't want to prevent initialization after timeout because LD will try still
    // connect in the background, the user will just be using the app with default flag values.
  }
};

/**
 * Update the LaunchDarkly client context for the current user.
 */
const identify = async (userContext: LDSingleKindContext): Promise<void> => {
  const client = await getInitializedClient();

  await client.identify(userContext);
};

type LdContextState = { client: LDClient | null };
const defaultLdContext = { client: null };
const LdClientContext = createContext<LdContextState>(defaultLdContext);

/**
 * Provide the LaunchDarkly client to React components once initialized.
 */
export function LdProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LdContextState>(defaultLdContext);

  useEffect(() => {
    if (state.client) {
      return;
    }

    (async () => {
      const client = await _client;
      setState({ client });
    })();
  }, [state.client]);

  useEffect(() => {
    return () => {
      state.client?.flush();
    };
  }, [state.client]);

  return <LdClientContext.Provider value={state}>{children}</LdClientContext.Provider>;
}

export { getUserContext } from './utils';
export const clearUserContext = getClearUserContext(identify);
export const updateUserContext = getUpdateUserContext(identify);

export { getAllFlagsSync, getFlagSync, useAllFlags, useFlag } from './flag-cache';
